import {config as fallbackConfig} from '../config/jewelleryLandingConfig';

const API_URL = import.meta.env.VITE_JEWELLERY_CMS_API_URL;
const PREVIEW_API_URL = import.meta.env.VITE_JEWELLERY_PREVIEW_API_URL;

const PRICING_CACHE_KEY = 'bpa_jewellery_pricing_cache_v1';
const LOCATIONS_CACHE_KEY = 'bpa_jewellery_locations_cache_v1';
const PRICING_CACHE_EXPIRY = 60000; // 60 seconds
const LOCATIONS_CACHE_EXPIRY = 86400000; // 24 hours

// State Normaliser map
const stateNormaliser = {
  'NSW': 'New South Wales',
  'VIC': 'Victoria',
  'QLD': 'Queensland',
  'WA': 'Western Australia',
  'SA': 'South Australia',
  'TAS': 'Tasmania',
  'ACT': 'Australian Capital Territory',
  'NT': 'Northern Territory',
};

const normalizeStateName = (st) => {
  if (!st) return 'Other';
  const trimmed = st.trim().toUpperCase();
  return stateNormaliser[trimmed] || st.trim();
};

// Offer active check - handles multiple truthy formats
const isOfferActive = (value) => {
  if (!value) return false;
  const normalized = String(value).toLowerCase().trim();
  return normalized === 'true' || normalized === 'yes' || normalized === 'y' || normalized === '1' || normalized === 'active';
};

// Remove old cache keys
const cleanupOldCacheKeys = () => {
  const oldKeys = [
    'bpa_cms_cache',
    'bpa_jewellery_cms_cache',
    'bpa_jewellery_cms_cache_v5',
    'bpa_jewellery_cms_cache_v6',
    'bpa_jewellery_cms_cache_v7',
    'bpa_jewellery_cms_cache_v8',
  ];
  oldKeys.forEach(key => localStorage.removeItem(key));
};

// Check for manual refresh parameter
const shouldForceRefresh = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('refreshCms') === 'true';
};

export const cmsService = {
  // Pricing fetch with aggressive refresh (60 second TTL)
  async fetchPricing() {
    let cachedData = null;
    try {
      cleanupOldCacheKeys();
      const forceRefresh = shouldForceRefresh();

      // Check cache
      const cached = localStorage.getItem(PRICING_CACHE_KEY);
      if (cached && !forceRefresh) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < PRICING_CACHE_EXPIRY) {
          cachedData = data;
        }
      }

      // Log URL status in dev mode
      if (import.meta.env.DEV) {
        console.log('[CMS] Pricing URL configured:', !API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID'));
      }

      // Fetch fresh data if needed
      if (!cachedData || forceRefresh) {
        if (!API_URL || API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
          throw new Error('API URL not configured');
        }

        const response = await fetch(API_URL, {
          method: 'GET',
          mode: 'cors',
        });

        const result = await response.json();
        const isSuccess = result.ok === true || result.success === true;
        const root = result.data || result;

        if (isSuccess) {
          // Normalize pricing with offer fields
          const rawPricing = root.pricing || root.pricingBundles || [];
          const normalizedPricing = rawPricing
            .filter(p => p.active !== false && p.active !== 'FALSE')
            .map(p => ({
              packageID: p.packageID || p.PackageID || p.id,
              packageName: p.packageName || p.PackageName || p.tier,
              tier: p.packageName || p.PackageName || p.tier,
              priceText: p.priceText || p.PriceText || '',
              originalPriceText: p.originalPriceText || p.OriginalPriceText || '',
              offerText: p.offerText || p.OfferText || '',
              offerActive: isOfferActive(p.offerActive || p.OfferActive),
              offerEndsAt: p.offerEndsAt || p.OfferEndsAt || '',
              shortDescription: p.shortDescription || p.ShortDescription || p.description,
              description: p.shortDescription || p.ShortDescription || p.description,
              includes: p.includes || p.Includes || p.features,
              features: p.includes || p.Includes || p.features,
              tag: p.tag || p.Tag || '',
              buttonText: p.buttonText || p.ButtonText || 'Enquire Now',
              checkoutURL: p.checkoutURL || p.CheckoutURL || p.checkoutUrl || '',
              checkoutUrl: p.checkoutURL || p.CheckoutURL || p.checkoutUrl || '',
              active: p.active !== false && p.active !== 'FALSE',
              sortOrder: p.sortOrder || p.SortOrder || 0,
            }))
            .sort((a, b) => a.sortOrder - b.sortOrder);

          const dataToCache = normalizedPricing.length > 0 ? normalizedPricing : fallbackConfig.pricing;

          // Cache the fresh data
          localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify({
            data: dataToCache,
            timestamp: Date.now(),
          }));

          if (import.meta.env.DEV) {
            console.log('[CMS] Pricing source: fresh');
            console.log('[CMS] Pricing bundles loaded:', dataToCache.length);
            console.log('[CMS] Last pricing refresh timestamp:', new Date().toISOString());
          }

          return dataToCache;
        }
        throw new Error(result.error || result.message || 'CMS response unsuccessful');
      }

      // Return cached data
      if (cachedData) {
        if (import.meta.env.DEV) {
          console.log('[CMS] Pricing source: cached');
          console.log('[CMS] Pricing bundles loaded:', cachedData.length);
        }
        return cachedData;
      }
      throw new Error('No cached data available');
    } catch (error) {
      // If fetch fails but we have cached data, use it
      if (cachedData) {
        if (import.meta.env.DEV) {
          console.warn('[CMS] Pricing fetch failed, using cached data:', error.message);
          console.log('[CMS] Pricing source: cached');
          console.log('[CMS] Pricing bundles loaded:', cachedData.length);
        }
        return cachedData;
      }
      // Otherwise use fallback
      if (import.meta.env.DEV) {
        console.warn('[CMS] Pricing fetch failed, no cache available, using fallback:', error.message);
        console.log('[CMS] Pricing source: fallback');
        console.log('[CMS] Pricing bundles loaded:', fallbackConfig.pricing.length);
      }
      return fallbackConfig.pricing;
    }
  },

  // Locations fetch with 24 hour cache
  async fetchLocations() {
    let cachedData = null;
    try {
      cleanupOldCacheKeys();
      const forceRefresh = shouldForceRefresh();

      // Check cache
      const cached = localStorage.getItem(LOCATIONS_CACHE_KEY);
      if (cached && !forceRefresh) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < LOCATIONS_CACHE_EXPIRY) {
          cachedData = data;
        }
      }

      // Log URL status in dev mode
      if (import.meta.env.DEV) {
        console.log('[CMS] Locations URL configured:', !API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID'));
      }

      // Fetch fresh data if needed
      if (!cachedData || forceRefresh) {
        if (!API_URL || API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
          throw new Error('API URL not configured');
        }

        const response = await fetch(API_URL, {
          method: 'GET',
          mode: 'cors',
        });

        const result = await response.json();
        const isSuccess = result.ok === true || result.success === true;
        const root = result.data || result;

        if (isSuccess) {
          // Normalize locations with strict deduplication
          const rawLocations = root.locationGroups || root.seoLocations || root.locations || [];
          const stateMap = new Map();

          const addSuburbToGroup = (stateName, groupName, suburbName) => {
            if (!suburbName || !suburbName.trim()) return;
            const trimmedSuburb = suburbName.trim();
            const groupMap = stateMap.get(stateName);
            const suburbSet = groupMap.get(groupName);
            if (!suburbSet.has(trimmedSuburb)) {
              suburbSet.add(trimmedSuburb);
            }
          };

          const processFormatA = (loc) => {
            const stateName = normalizeStateName(loc.state);
            const groupName = (loc.displayGroup || loc.region || 'Regional').trim();
            const suburbName = loc.suburb;
            if (!stateMap.has(stateName)) {
              stateMap.set(stateName, new Map());
            }
            const groupMap = stateMap.get(stateName);
            if (!groupMap.has(groupName)) {
              groupMap.set(groupName, new Set());
            }
            if (suburbName) {
              addSuburbToGroup(stateName, groupName, suburbName);
            }
          };

          const processFormatB = (loc) => {
            const stateName = normalizeStateName(loc.state);
            const groupName = (loc.displayGroup || loc.region || 'Regional').trim();
            const suburbsCSV = loc.suburbsCSV || loc.suburbs || '';
            if (!stateMap.has(stateName)) {
              stateMap.set(stateName, new Map());
            }
            const groupMap = stateMap.get(stateName);
            if (!groupMap.has(groupName)) {
              groupMap.set(groupName, new Set());
            }
            if (suburbsCSV) {
              const suburbs = suburbsCSV.split(',')
                .map(s => s.trim())
                .filter(s => s.length > 0);
              suburbs.forEach(suburb => {
                addSuburbToGroup(stateName, groupName, suburb);
              });
            }
          };

          const processFormatC = (loc) => {
            const stateName = normalizeStateName(loc.state);
            if (!stateMap.has(stateName)) {
              stateMap.set(stateName, new Map());
            }
            const groupMap = stateMap.get(stateName);
            loc.displayGroups.forEach(group => {
              const groupName = group.groupName.trim();
              if (!groupMap.has(groupName)) {
                groupMap.set(groupName, new Set());
              }
              if (group.suburbs && Array.isArray(group.suburbs)) {
                group.suburbs.forEach(suburb => {
                  addSuburbToGroup(stateName, groupName, suburb);
                });
              }
            });
          };

          rawLocations.forEach(loc => {
            const active = loc.active !== false && loc.active !== 'FALSE';
            if (!active) return;
            if (loc.displayGroups && Array.isArray(loc.displayGroups)) {
              processFormatC(loc);
            } else if (loc.suburbsCSV || (typeof loc.suburbs === 'string' && loc.suburbs.includes(','))) {
              processFormatB(loc);
            } else {
              processFormatA(loc);
            }
          });

          const normalizedLocations = Array.from(stateMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([state, groupMap]) => ({
              state,
              displayGroups: Array.from(groupMap.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([groupName, suburbSet]) => ({
                  groupName,
                  suburbs: Array.from(suburbSet).sort((a, b) => a.localeCompare(b))
                }))
            }));

          // Only use fallback if no locations from CMS
          const dataToCache = normalizedLocations.length > 0 ? normalizedLocations : (forceRefresh ? [] : fallbackConfig.locations);

          // Cache the fresh data
          localStorage.setItem(LOCATIONS_CACHE_KEY, JSON.stringify({
            data: dataToCache,
            timestamp: Date.now(),
          }));

          if (import.meta.env.DEV) {
            console.log('[CMS] Locations source: fresh');
            console.log('[CMS] Location states loaded:', dataToCache.length);
          }

          return dataToCache;
        }
        throw new Error(result.error || result.message || 'CMS response unsuccessful');
      }

      // Return cached data
      if (cachedData) {
        if (import.meta.env.DEV) {
          console.log('[CMS] Locations source: cached');
          console.log('[CMS] Location states loaded:', cachedData.length);
        }
        return cachedData;
      }
      throw new Error('No cached data available');
    } catch (error) {
      // If fetch fails but we have cached data, use it
      if (cachedData) {
        if (import.meta.env.DEV) {
          console.warn('[CMS] Locations fetch failed, using cached data:', error.message);
          console.log('[CMS] Locations source: cached');
          console.log('[CMS] Location states loaded:', cachedData.length);
        }
        return cachedData;
      }
      // Otherwise use fallback
      if (import.meta.env.DEV) {
        console.warn('[CMS] Locations fetch failed, no cache available, using fallback:', error.message);
        console.log('[CMS] Locations source: fallback');
        console.log('[CMS] Location states loaded:', fallbackConfig.locations.length);
      }
      return fallbackConfig.locations;
    }
  },

  // Combined fetch for backwards compatibility
  async fetchData() {
    const [pricing, locations] = await Promise.all([
      this.fetchPricing(),
      this.fetchLocations(),
    ]);
    return { pricing, locations };
  },

  // Start polling for pricing updates (every 60 seconds)
  startPricingPolling(callback) {
    const poll = async () => {
      try {
        const pricing = await this.fetchPricing();
        if (callback) callback(pricing);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error('[CMS] Pricing poll error:', error.message);
        }
      }
    };

    // Poll every 60 seconds
    const intervalId = setInterval(poll, 60000);

    // Refresh when tab becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        if (import.meta.env.DEV) {
          console.log('[CMS] Tab became visible, refreshing pricing');
        }
        poll();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  },

  // NEW: Preview Portal Methods
  async validatePreviewAdmin(adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'validateAdmin',
          adminPasscode: adminPasscode.trim(),
        }),
      });

      const result = await response.json();
      const isAuthenticated = result.ok === true &&
        result.service === 'BPA Luxury Preview Room CMS' &&
        result.version === '2.1' &&
        result.data?.authenticated === true &&
        result.data?.authMode === 'server';

      if (isAuthenticated) {
        return { success: true };
      }

      if (
        result.ok === true &&
        (
          result.service !== 'BPA Luxury Preview Room CMS' ||
          result.version !== '2.1' ||
          result.data?.authenticated !== true ||
          result.data?.authMode !== 'server'
        )
      ) {
        return { success: false, error: 'Preview API deployment is outdated or incorrect. Redeploy Google Apps Script V2.1.' };
      }

      return { success: false, error: result.error || result.message || 'Invalid admin passcode.' };
    } catch (error) {
      return { success: false, error: error.message || 'Network error during admin validation.' };
    }
  },

  async listPreviews(adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'listPreviews',
          adminPasscode: adminPasscode.trim(),
        }),
      });

      const result = await response.json();
      const isSuccess = result.ok === true || result.success === true;

      if (isSuccess) {
        return { success: true, data: result.data?.previews || result.previews || [] };
      }

      return { success: false, error: result.error || result.message || 'Failed to load previews.' };
    } catch (error) {
      return { success: false, error: 'Network error while fetching previews.' };
    }
  },

  async savePreview(payload, adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'savePreview',
          adminPasscode: adminPasscode.trim(),
          data: payload,
        }),
      });

      const result = await response.json();
      const isSuccess = result.ok === true || result.success === true;

      if (isSuccess) {
        return { success: true, data: result.data || result };
      }

      return { success: false, error: result.error || result.message || 'Failed to save preview.' };
    } catch (error) {
      return { success: false, error: 'Network error while saving preview.' };
    }
  },

  async fetchPreview(previewId) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const url = `${PREVIEW_API_URL}?action=getPreview&preview_id=${encodeURIComponent(previewId)}`;
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
      });

      const result = await response.json();
      const isSuccess = result.ok === true || result.success === true;

      if (isSuccess) {
        return { success: true, data: result.data || result.preview };
      }

      return { success: false, error: result.error || result.message || 'Preview not found.' };
    } catch (error) {
      return { success: false, error: 'Network error while fetching preview.' };
    }
  },

  async logPreviewEvent({ eventType, previewId, details = {} }) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'logPreviewEvent',
          event_type: eventType,
          preview_id: previewId,
          details,
        }),
      });

      const result = await response.json();
      if (result.ok === true || result.success === true) {
        return { success: true };
      }
      return { success: false, error: result.error || result.message || 'Unable to record your response.' };
    } catch (error) {
      return { success: false, error: 'Network error while recording your response.' };
    }
  },

  async getPreviewShareDraft(previewId, adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'getPreviewShareDraft',
          adminPasscode: adminPasscode.trim(),
          previewId,
        }),
      });

      const result = await response.json();
      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }
      return { success: false, error: result.error || result.message || 'Unable to load share draft.' };
    } catch (error) {
      return { success: false, error: 'Network error while loading share draft.' };
    }
  },

  async sendPreviewEmail(payload, adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'sendPreviewEmail',
          adminPasscode: adminPasscode.trim(),
          ...payload,
        }),
      });

      const result = await response.json();
      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }
      return { success: false, error: result.error || result.message || 'Unable to send preview email.' };
    } catch (error) {
      return { success: false, error: 'Network error while sending preview email.' };
    }
  },

  async sendPreviewSms(payload, adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'sendPreviewSms',
          adminPasscode: adminPasscode.trim(),
          ...payload,
        }),
      });

      const result = await response.json();
      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }
      return { success: false, error: result.error || result.message || 'Unable to send preview SMS.' };
    } catch (error) {
      return { success: false, error: 'Network error while sending preview SMS.' };
    }
  },

  async getPreviewForAdmin(previewId, adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'getPreviewForAdmin',
          previewId,
          adminPasscode: adminPasscode.trim(),
        }),
      });

      const result = await response.json();
      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }
      return { success: false, error: result.error || result.message || 'Unable to load preview for editing.' };
    } catch (error) {
      return { success: false, error: 'Network error while loading preview for editing.' };
    }
  },

  async archivePreview(previewId, adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'archivePreview',
          previewId,
          adminPasscode: adminPasscode.trim(),
        }),
      });

      const result = await response.json();
      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }
      return { success: false, error: result.error || result.message || 'Unable to archive preview.' };
    } catch (error) {
      return { success: false, error: 'Network error while archiving preview.' };
    }
  },

  async restorePreview(previewId, adminPasscode) {
    if (!PREVIEW_API_URL || PREVIEW_API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
      return { success: false, error: 'Preview API URL is not configured.' };
    }

    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({
          action: 'restorePreview',
          previewId,
          adminPasscode: adminPasscode.trim(),
        }),
      });

      const result = await response.json();
      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }
      return { success: false, error: result.error || result.message || 'Unable to restore preview.' };
    } catch (error) {
      return { success: false, error: 'Network error while restoring preview.' };
    }
  },

  async listIntroOutreachPreviews(adminPasscode) {
    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          action: 'listIntroOutreachPreviews',
          adminPasscode: adminPasscode.trim(),
        }),
      });

      const result = await response.json();

      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }

      return {
        success: false,
        error:
          result.error ||
          result.message ||
          'Unable to load approved outreach previews.',
      };
    } catch (error) {
      return {
        success: false,
        error:
          'Network error while loading approved outreach previews.',
      };
    }
  },

  async setPreviewOutreachApproved(
    previewId,
    outreachApproved,
    adminPasscode
  ) {
    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          action: 'setPreviewOutreachApproved',
          previewId,
          outreachApproved,
          adminPasscode: adminPasscode.trim(),
        }),
      });

      const result = await response.json();

      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }

      return {
        success: false,
        error:
          result.error ||
          result.message ||
          'Unable to update outreach approval.',
      };
    } catch (error) {
      return {
        success: false,
        error:
          'Network error while updating outreach approval.',
      };
    }
  },

  async getIntroOutreachDraft(payload, adminPasscode) {
    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          action: 'getIntroOutreachDraft',
          adminPasscode: adminPasscode.trim(),
          ...payload,
        }),
      });

      const result = await response.json();

      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }

      return {
        success: false,
        error:
          result.error ||
          result.message ||
          'Unable to generate introductory outreach draft.',
      };
    } catch (error) {
      return {
        success: false,
        error:
          'Network error while generating introductory outreach draft.',
      };
    }
  },

  async sendIntroOutreachEmail(payload, adminPasscode) {
    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          action: 'sendIntroOutreachEmail',
          adminPasscode: adminPasscode.trim(),
          ...payload,
        }),
      });

      const result = await response.json();

      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }

      return {
        success: false,
        error:
          result.error ||
          result.message ||
          'Unable to send introductory email.',
      };
    } catch (error) {
      return {
        success: false,
        error:
          'Network error while sending introductory email.',
      };
    }
  },

  async sendIntroOutreachSms(payload, adminPasscode) {
    try {
      const response = await fetch(PREVIEW_API_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({
          action: 'sendIntroOutreachSms',
          adminPasscode: adminPasscode.trim(),
          ...payload,
        }),
      });

      const result = await response.json();

      if (result.ok === true || result.success === true) {
        return {
          success: true,
          data: result.data || result,
        };
      }

      return {
        success: false,
        error:
          result.error ||
          result.message ||
          'Unable to send introductory SMS.',
      };
    } catch (error) {
      return {
        success: false,
        error:
          'Network error while sending introductory SMS.',
      };
    }
  },

  async submitLead(payload) {
    try {
      if (!API_URL || API_URL.includes('YOUR_DEPLOYED_SCRIPT_ID')) {
        return { success: false, error: 'The enquiry endpoint is not configured yet.' };
      }

      try {
        const corsResponse = await fetch(API_URL, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'text/plain',
          },
          body: JSON.stringify(payload),
        });

        const data = await corsResponse.json();
        const isSuccess = data.ok === true || data.success === true;

        if (!isSuccess) {
          return { success: false, error: data.error || data.message || 'Submission was rejected by the server.' };
        }
        return { success: true, mode: 'cors' };
      } catch (corsError) {
        await fetch(API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'text/plain',
          },
          body: JSON.stringify(payload),
        });
        return { success: true, mode: 'no-cors' };
      }
    } catch (error) {
      return { success: false, error: 'Network issue occurred while submitting.' };
    }
  },
};